import { FormattedMessage, useIntl } from 'react-intl';
import { numberFormat } from '../../../../helpers/utils';
import { formatCurrencyExtended } from '../../../../helpers/currencyFormatter';
import {
  FeatureCell,
  Separator,
  ValuationBreakdownDotMax,
  ValuationBreakdownDotMin,
  ValuationContainer,
  ValuationMainPrice,
  ValuationMainPriceContainer,
  ValuationMainPriceSufix,
  ValuationMarketSituationContainer,
  ValuationMarketSituationContent,
  ValuationMarketSituationDataBlock,
  ValuationMarketSituationDataBlockLabel,
  ValuationMarketSituationDataBlockValue,
  ValuationMarketSituationDataBlockValueContainer,
  ValuationMarketSituationDataBlockValueSufix,
  ValuationMarketSituationDataBlocks,
  ValuationMarketSituationTitle,
  ValuationPricesBreakdown,
  ValuationPricesBreakdownPartLabel,
  ValuationPricesBreakdownPartMax,
  ValuationPricesBreakdownPartMin,
  ValuationPricesContainer,
  ValuationPricesTopRow,
  ValuationPricesMain,
  ValuationSeparator,
  ValuationSqmPrice,
  ValuationStatusPrice,
  ValuationStatusPriceLabel,
  ValuationStatusPriceValue,
  ValuationTitle,
  ValuationYieldContainer,
  ValuationYieldLabel,
  ValuationYieldValue,
} from './valuationSubmodules.styles';
import { SaleProfile } from '../../../../types/valuation.interface';
import { getMethodCellColor } from '../valuation.module';
import { SocialFinancialCards } from '../../ReportFinancial/SocialFinancialCards';
import { SocialFinancialDataModel } from '../../../../types/indicators.interfaces';
import { SectionTitle } from '../../../../styles/global.styles';
import React, { useState } from 'react';
import { NoDataContainer } from '../../NoDataComponent/noData.styles';
import NoDataIcon from '../../../../assets/images/error.png';
import { isLatamCountry } from '../../../../helpers/countries';

export function SaleValuation({
  isExcluded,
  data,
  reportType,
  operation,
  listingType,
  social_financial,
  country = 'es',
  otherOperationFailed,
  yieldValue,
}: {
  isExcluded: boolean;
  data: SaleProfile;
  reportType: number;
  estimatedMin: number | null;
  estimatedMax: number | null;
  operation: number;
  listingType?: number;
  social_financial?: SocialFinancialDataModel[] | null;
  country?: string;
  otherOperationFailed?: boolean;
  /** When present (rent report with yield), show blue yield container below sale price */
  yieldValue?: number;
}) {
  const intl = useIntl();
  const [showTooltip, setShowTooltip] = useState(false);

  // Check if we should show social financial cards instead of market situation
  const shouldShowSocialFinancial =
    (reportType === 2 || reportType === 3) &&
    listingType &&
    (listingType === 8 || listingType === 12);

  // En LATAM no se muestra el bloque de situación de mercado. Se deja su hueco en la rejilla
  // (60% / 40%) para no descuadrar la página del informe.
  const isLatam = isLatamCountry(country);

  const personalisedReport = reportType === 2 && operation === 1;

  const sqmSuffix = intl.formatMessage({ id: 'sqmPriceSuffix' });

  return (
    <ValuationContainer
      isexcluded={isExcluded.toString()}
      aria-label="valuation-container"
    >
      <ValuationPricesContainer
        aria-label="valuation-prices-container"
        pricesBreakdown={
          (data.priceRange === null && !shouldShowSocialFinancial) &&
          yieldValue == null
        }
      >
        <ValuationPricesTopRow>
        <ValuationPricesMain aria-label="valuation-prices-main">
          <ValuationTitle>
            <FormattedMessage
              id={yieldValue != null ? 'valuation.salePriceBasedOnYield' : 'valuation.salePriceTitle'}
            />
          </ValuationTitle>
          {otherOperationFailed ? (
            <NoDataContainer>
              <img src={NoDataIcon} />
              Valuation not provided due to lack of data, you can exclude this field in configure modules settings ⚙️ 
            </NoDataContainer>
          ) : (
            <ValuationMainPriceContainer>
              <ValuationMainPrice
                operation={1}
                pricesBreakdown={
                  data.priceRange !== null &&
                  !personalisedReport &&
                  yieldValue == null
                }
              >
                {data.mainValue ? (
                  <>
                    {formatCurrencyExtended(
                      data.mainValue,
                      country,
                      false,
                      sqmSuffix,
                      undefined,
                      true,
                      1000000000,
                      false,
                      true
                    )}
                    <ValuationMainPriceSufix
                      operation={1}
                      pricesBreakdown={data.priceRange !== null && yieldValue == null}
                    >
                      {formatCurrencyExtended(
                        data.mainValue,
                        country,
                        false,
                        sqmSuffix,
                        undefined,
                        undefined,
                        1000000000,
                        true
                      )}
                    </ValuationMainPriceSufix>
                  </>
                ) : (
                  '- '
                )}
              </ValuationMainPrice>
              <ValuationSqmPrice>
                {data.sqmPrice ? (
                  formatCurrencyExtended(
                    data.sqmPrice,
                    country,
                    true,
                    sqmSuffix,
                    undefined,
                    undefined,
                    1000000
                  )
                ) : (
                  '- '
                )}
              </ValuationSqmPrice>
            </ValuationMainPriceContainer>
          )}
          {(() => {
            const colors = data.bestMethod
              ? getMethodCellColor(data.bestMethod)
              : null;
            return colors?.backgroundcolor &&
              colors?.color &&
              yieldValue == null &&
              (reportType !== 2 ||
                ((reportType === 2 || reportType === 3) && operation === 0)) ? (
              <FeatureCell
                backgroundcolor={colors.backgroundcolor}
                color={colors.color}
              >
                <FormattedMessage id={`valuation.${data.bestMethod}`} />
              </FeatureCell>
            ) : null;
          })()}
          {data?.priceRange !== null && yieldValue == null && (
            <ValuationPricesBreakdown aria-label="valuation-prices-breakdown">
              <ValuationPricesBreakdownPartMin>
                <ValuationPricesBreakdownPartLabel>
                  <ValuationBreakdownDotMin />
                  <FormattedMessage id="min" />
                </ValuationPricesBreakdownPartLabel>
                <span>
                  {data.priceRange && data.priceRange[0]
                    ? personalisedReport
                      ? (() => {
                          const factor = data.mainValue * 0.1;
                          const valor = data.mainValue - factor;
                          return formatCurrencyExtended(
                            valor,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceRange[0],
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </span>
              </ValuationPricesBreakdownPartMin>
              <ValuationPricesBreakdownPartMax>
                <ValuationPricesBreakdownPartLabel>
                  <ValuationBreakdownDotMax />
                  <FormattedMessage id="max" />
                </ValuationPricesBreakdownPartLabel>
                <span>
                  {data.priceRange && data.priceRange[1]
                    ? personalisedReport
                      ? (() => {
                          const factor = data.mainValue * 0.1;
                          const valor = data.mainValue + factor;
                          return formatCurrencyExtended(
                            valor,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceRange[1],
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </span>
              </ValuationPricesBreakdownPartMax>
            </ValuationPricesBreakdown>
          )}
        </ValuationPricesMain>
        {yieldValue == null && (
          <ValuationSeparator
            aria-label="valaution-separator"
            show={data?.priceByStatus}
          >
            {data?.priceByStatus && (
            <>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.6" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_6
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue * 1.1125;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_6,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.1" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_1
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_1,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.2" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_2
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue * 0.925;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_2,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.5" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_5
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue * 0.85;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_5,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
            </>
          )}
          </ValuationSeparator>
        )}
        </ValuationPricesTopRow>
        {yieldValue != null && (
          <ValuationYieldContainer aria-label="yield-container">
            <ValuationYieldLabel>
              <FormattedMessage id="valuation.yieldLabel" defaultMessage="Yield" />
            </ValuationYieldLabel>
            <ValuationYieldValue>
              {numberFormat.format(yieldValue)}%
            </ValuationYieldValue>
          </ValuationYieldContainer>
        )}
      </ValuationPricesContainer>
      {shouldShowSocialFinancial ? (
        <div style={{ marginTop: '-70px' }}>
          <SocialFinancialCards
            social_financial={social_financial || null}
            country={country}
          />
        </div>
      ) : (
        !isLatam && (
          <ValuationMarketSituationContainer>
            <ValuationMarketSituationContent>
              <ValuationMarketSituationTitle>
                <FormattedMessage id="valuation.saleMarketSituation" />
              </ValuationMarketSituationTitle>
              <ValuationMarketSituationDataBlocks>
                <ValuationMarketSituationDataBlock operation={1}>
                  <ValuationMarketSituationDataBlockLabel>
                    <FormattedMessage id="valuation.avgSaleTime" />
                  </ValuationMarketSituationDataBlockLabel>
                  <ValuationMarketSituationDataBlockValueContainer>
                    <ValuationMarketSituationDataBlockValue>
                      {data.timeOnMarket ? data.timeOnMarket : '- '}
                    </ValuationMarketSituationDataBlockValue>
                    <ValuationMarketSituationDataBlockValueSufix>
                      Sem.
                    </ValuationMarketSituationDataBlockValueSufix>
                  </ValuationMarketSituationDataBlockValueContainer>
                </ValuationMarketSituationDataBlock>
                <ValuationMarketSituationDataBlock operation={1}>
                  <ValuationMarketSituationDataBlockLabel>
                    <FormattedMessage id="valuation.negotiationLeeway" />
                  </ValuationMarketSituationDataBlockLabel>
                  <ValuationMarketSituationDataBlockValueContainer>
                    <ValuationMarketSituationDataBlockValue>
                      {data.negotiationLeeway ? data.negotiationLeeway : '- '}
                    </ValuationMarketSituationDataBlockValue>
                    <ValuationMarketSituationDataBlockValueSufix>
                      %
                    </ValuationMarketSituationDataBlockValueSufix>
                  </ValuationMarketSituationDataBlockValueContainer>
                </ValuationMarketSituationDataBlock>
              </ValuationMarketSituationDataBlocks>
            </ValuationMarketSituationContent>
          </ValuationMarketSituationContainer>
        )
      )}
    </ValuationContainer>
  );
}
